import { Message, Sms, Whatsapp } from 'iconsax-react';
import type { ColumnDef } from '@tanstack/react-table';

import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { Text } from '@src/design-system/text';
import {
  REMINDER_CHANNELS,
  REMINDER_TYPES,
  type TReminderLog,
} from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { Badge } from '@src/design-system/badge';
import { getRecipientDisplay } from '../../get-recipient-display';

function formatSentAt(isoDate: string) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function normalizeChannel(ch: string) {
  return ch.trim().toUpperCase();
}

function ChannelCell({ channel }: { channel: string }) {
  const c = normalizeChannel(channel);
  if (c === REMINDER_CHANNELS.whatsapp || c === 'WHATSAPP') {
    return (
      <span className='inline-flex items-center gap-1 text-sm font-medium text-gray-1000'>
        <Whatsapp
          size={20}
          variant='Bold'
          color='#2EA844'
        />
        {formatTextForDisplay(channel)}
      </span>
    );
  }
  if (c === REMINDER_CHANNELS.sms || c === 'SMS') {
    return (
      <span className='inline-flex items-center gap-1 text-sm font-medium text-gray-1000'>
        <Message
          size={20}
          variant='Bold'
          color='#3B82F6'
        />

        {formatTextForDisplay(channel)}
      </span>
    );
  }
  return (
    <span className='inline-flex items-center gap-1 text-sm font-medium text-gray-1000'>
      <Sms
        size={20}
        variant='Bold'
        color='#DE483B'
      />
      {formatTextForDisplay(channel)}
    </span>
  );
}

function getReminderTypeLabel(type: string): string {
  const bulkTypes: Set<string> = new Set([
    REMINDER_TYPES.bulk,
    REMINDER_TYPES.bulkStage,
    REMINDER_TYPES.bulkLead,
    REMINDER_TYPES.bulkApplication,
  ]);
  return bulkTypes.has(type) ? 'Bulk Reminder' : 'Automated Reminder';
}

export const failedRemindersColumns: ColumnDef<TReminderLog>[] = [
  {
    id: 'sentAt',
    accessorKey: 'created_at',
    header: 'Sent At',
    cell: ({ row }) => (
      <span className='text-sm text-gray-1000'>
        {formatSentAt(row.original.created_at)}
      </span>
    ),
  },
  {
    id: 'reminderName',
    accessorFn: (row) => row.reminder.name,
    header: 'Reminder Name',
    cell: ({ row }) => (
      <span className='text-sm font-medium text-gray-1000'>
        {row.original.reminder.name}
      </span>
    ),
  },
  {
    id: 'recipientName',
    accessorFn: (row) => getRecipientDisplay(row).name,
    header: 'Recipient Name',
    cell: ({ row }) => {
      const { name, avatarUrl } = getRecipientDisplay(row.original);

      return (
        <div className='flex items-center gap-2'>
          <Avatar className='size-5 rounded-[4px] bg-active-border'>
            <AvatarImage src={avatarUrl} />
            <AvatarFallback>{name.slice(0, 1) || '?'}</AvatarFallback>
          </Avatar>
          <Text className='truncate text-sm'>{name}</Text>
        </div>
      );
    },
  },
  {
    id: 'channel',
    accessorKey: 'channel',
    header: 'Channel',
    enableSorting: false,
    cell: ({ row }) => <ChannelCell channel={row.original.channel} />,
  },
  {
    id: 'reminderType',
    accessorFn: (row) => row.reminder.type,
    header: 'Type',
    cell: ({ row }) => (
      <span className='text-sm text-gray-1000'>
        {getReminderTypeLabel(String(row.original.reminder.type))}
      </span>
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => (
      <Badge className='bg-pink-100 text-pink-500 w-[58px] h-[32px] rounded-xl'>
        {formatTextForDisplay(row.original.status)}
      </Badge>
    ),
  },
  {
    id: 'failureReason',
    accessorKey: 'failure_reason',
    header: 'Reason',
    cell: ({ row }) => (
      <span className='text-sm text-gray-800'>
        {row.original.failure_reason?.trim() || '—'}
      </span>
    ),
  },
];
